import { useState, type FormEvent } from 'react';
import type { ItemResponse, ListResponse, RoutineItem } from '@jarvis/shared';
import { Icon } from '../components/Icon';
import { toast } from '../components/toast';
import { CardHead, ErrorState, Skeleton } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLive } from '../lib/useLive';

const byTime = (a: RoutineItem, b: RoutineItem) => a.time.localeCompare(b.time);

/* Rotina definida pelo usuário: não depende de nenhuma integração. */
export function RoutineLive() {
  const { state, set, reload } = useLive('/api/routine', (b: ListResponse<RoutineItem>) => b.items);
  const [time, setTime] = useState('07:00');
  const [label, setLabel] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const r = await api.post<ItemResponse<RoutineItem>>('/api/routine', { time, label });
      set((list) => [...list, r.item].sort(byTime));
      setLabel('');
    } catch (err) {
      if (err instanceof ApiError && err.fields) setErrors(err.fields);
      else toast(err instanceof Error ? err.message : 'Algo deu errado.', 'alert');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item: RoutineItem) => {
    try {
      await api.del(`/api/routine/${item.id}`);
      set((list) => list.filter((x) => x.id !== item.id));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Algo deu errado.', 'alert');
    }
  };

  return (
    <section className="card">
      <CardHead icon="sun" title="Sua rotina" />
      {state.status === 'loading' && <Skeleton kind="line" />}
      {state.status === 'error' && <ErrorState error={state.error} onRetry={reload} compact />}
      {state.status === 'ready' && (
        state.data.length
          ? <div className="routine">{state.data.map((r) => (
              <div key={r.id} className="routine-item">
                <span className="time-pill">{r.time}</span><span className="row-main">{r.label}</span>
                <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remover ${r.label}`} title="Remover" onClick={() => remove(r)}><Icon name="trash" /></button>
              </div>))}
            </div>
          : <p className="row-sub">Nenhum item ainda. Adicione horários e hábitos do seu dia.</p>
      )}
      {state.status === 'ready' && (
        <form className="inline-form mt-10" onSubmit={add} noValidate>
          <label className="sr-only" htmlFor="rt-time">Horário</label>
          <input id="rt-time" className="input input-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-invalid={errors.time ? true : undefined} />
          <label className="sr-only" htmlFor="rt-label">Descrição</label>
          <input id="rt-label" className="input" placeholder="Ex.: Caminhada" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} aria-invalid={errors.label ? true : undefined} />
          <button type="submit" className="btn btn-sm" disabled={busy || !label.trim()}>Adicionar</button>
        </form>
      )}
      {(errors.time || errors.label) && <p className="field-error">{errors.time ?? errors.label}</p>}
    </section>
  );
}
